import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { centimes, dateFr, heureFr, montant } from '../lib/format';
import type { Mouvement, Tiroir } from '../api/types';

interface Tiroirs { tiroirs: Tiroir[]; totalReferenceMinor: number; deviseReference: string }

/**
 * Le compte en banque.
 *
 * La caisse et la banque sont deux poches differentes : on ne paie pas un
 * garagiste en especes avec de l'argent qui dort sur le compte. Deplacer de
 * l'argent entre les deux cree DEUX mouvements apparies — une sortie ici, une
 * entree la-bas — parce qu'un seul ferait apparaitre ou disparaitre de l'argent,
 * et c'est le genre d'ecart qu'on ne retrouve jamais trois mois plus tard.
 */
export default function Banque() {
  const { peut } = useAuth();
  const mobile = useMobile();
  const [t, setT] = useState<Tiroirs | null>(null);
  const [mvts, setMvts] = useState<Mouvement[] | null>(null);
  const [ecran, setEcran] = useState<'' | 'transfert' | 'frais'>('');
  const [msg, setMsg] = useState('');

  const recharger = useCallback(() => {
    api.get<Tiroirs>('/caisse/banque/tiroirs').then(setT).catch(() => {});
    api.get<Mouvement[]>('/caisse/banque/mouvements').then(setMvts).catch(() => setMvts([]));
  }, []);
  useEffect(recharger, [recharger]);

  if (!t || mvts === null) return <Squelette lignes={6} />;

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Banque</h1>
          <p>Le compte de l'agence · distinct de la caisse, et il doit le rester</p>
        </div>
        {peut('modifier.finance') && (
          <div className="actions">
            <button className="btn btn-p" onClick={() => setEcran('transfert')}>Versement / retrait</button>
            <button className="btn" onClick={() => setEcran('frais')}>Frais bancaires</button>
          </div>
        )}
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      <div className="tiroirs" style={{ marginBottom: 16 }}>
        {t.tiroirs.map((x) => (
          <div key={x.devise} className="carte carte-p">
            <div className="micro">{x.libelle} · {x.devise}</div>
            <div className="v montant">{montant(x.soldeMinor, x.devise, x.decimales)}</div>
            {x.devise !== t.deviseReference && (
              <div className="mini sourdine">≈ {montant(x.contrevaleurMinor)} </div>
            )}
          </div>
        ))}
        <div className="carte carte-p signature-kpi">
          <div className="micro">Total en banque</div>
          <div className="v">{montant(t.totalReferenceMinor)}</div>
        </div>
      </div>

      <Carte pad={false} titre="Mouvements du compte">
        {mvts.length === 0 ? (
          <div className="carte-p">
            <Vide titre="Aucun mouvement bancaire"
                  action={peut('modifier.finance')
                    ? <button className="btn btn-p btn-l" onClick={() => setEcran('transfert')}>Enregistrer un versement</button>
                    : undefined}>
              Déposez à la banque ce qui n'a pas à rester en caisse. Chaque versement crée deux
              lignes — une sortie de caisse, une entrée en banque — pour que rien n'apparaisse
              ni ne disparaisse.
            </Vide>
          </div>
        ) : mobile ? (
          <div className="cartes">
            {mvts.map((m) => (
              <div key={m._id} className="carte-ligne">
                <div className="cl-tete">
                  <span className="cl-titre">{m.libelle}</span>
                  <span className="montant">{montant(m.contrevaleurMinor)}</span>
                </div>
                <div className="cl-sous">{dateFr(m.date)} · {m.creePar?.nom ?? '—'}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead><tr><th>Date</th><th>Libellé</th><th>Référence</th><th className="droite">Montant</th><th>Par</th></tr></thead>
              <tbody>
                {mvts.map((m) => (
                  <tr key={m._id}>
                    <td className="mini sourdine">{dateFr(m.date)} <span className="num">{heureFr(m.date)}</span></td>
                    <td>{m.libelle}</td>
                    <td className="mini sourdine">{m.pieceJustificative ?? '—'}</td>
                    <td className="droite montant"
                        style={{ color: m.type === 'transfert_entree' ? 'var(--succes)' : 'var(--danger)' }}>
                      {m.type === 'transfert_entree' ? '+' : '−'}{montant(m.contrevaleurMinor).replace('−', '')}
                    </td>
                    <td className="mini sourdine">{m.creePar?.nom ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        Un versement apparaît des deux côtés : il sort de la caisse et entre en banque.
        Le total des deux poches ne change pas — c'est ce qui permet de retrouver un écart.
      </p>

      {ecran === 'transfert' && (
        <Transfert devise={t.deviseReference}
                   onFermer={() => setEcran('')}
                   onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} />
      )}
      {ecran === 'frais' && (
        <Frais devise={t.deviseReference}
               onFermer={() => setEcran('')}
               onFini={(m) => { setEcran(''); setMsg(m); recharger(); }} />
      )}
    </>
  );
}

function Transfert({ devise, onFini, onFermer }: { devise: string; onFini: (m: string) => void; onFermer: () => void }) {
  const [sens, setSens] = useState<'vers_banque' | 'vers_caisse'>('vers_banque');
  const [mt, setMt] = useState('');
  const [libelle, setLibelle] = useState('Dépôt en banque');
  const [reference, setReference] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  async function envoyer() {
    setErreur(''); setEnvoi(true);
    try {
      await api.post('/caisse/banque/transfert', {
        sens, devise, montantMinor: centimes(Number(mt)), libelle, reference: reference || undefined,
      });
      onFini(sens === 'vers_banque'
        ? `${montant(centimes(Number(mt)))} déposés en banque — la caisse a diminué d'autant.`
        : `${montant(centimes(Number(mt)))} retirés de la banque — la caisse a augmenté d'autant.`);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Opération impossible'); }
    finally { setEnvoi(false); }
  }

  return (
    <Modale titre="Versement / retrait" onFermer={onFermer}>
      <Champ label="Sens">
        <select value={sens} onChange={(e) => {
          const v = e.target.value as typeof sens;
          setSens(v);
          setLibelle(v === 'vers_banque' ? 'Dépôt en banque' : 'Retrait pour la caisse');
        }}>
          <option value="vers_banque">De la caisse vers la banque</option>
          <option value="vers_caisse">De la banque vers la caisse</option>
        </select>
      </Champ>
      <div className="grille g2" style={{ marginTop: 12 }}>
        <Champ label={`Montant (${devise})`}>
          <input type="number" inputMode="decimal" value={mt} onChange={(e) => setMt(e.target.value)} autoFocus />
        </Champ>
        <Champ label="Référence" aide="N° de bordereau, facultatif.">
          <input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Champ>
      </div>
      <div style={{ marginTop: 12 }}>
        <Champ label="Libellé"><input value={libelle} onChange={(e) => setLibelle(e.target.value)} /></Champ>
      </div>
      <div style={{ marginTop: 12 }}>
        <Bandeau type="info" titre="Deux lignes seront créées">
          Une sortie de {sens === 'vers_banque' ? 'la caisse' : 'la banque'} et une entrée
          en {sens === 'vers_banque' ? 'banque' : 'caisse'}. L'argent ne peut ni apparaître ni disparaître.
        </Bandeau>
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" disabled={!(Number(mt) > 0) || libelle.length < 2 || envoi}
                onClick={() => void envoyer()}>Enregistrer</button>
      </div>
    </Modale>
  );
}

function Frais({ devise, onFini, onFermer }: { devise: string; onFini: (m: string) => void; onFermer: () => void }) {
  const [mt, setMt] = useState('');
  const [libelle, setLibelle] = useState('Frais de tenue de compte');
  const [erreur, setErreur] = useState('');

  async function envoyer() {
    setErreur('');
    try {
      await api.post('/caisse/banque/frais', { devise, montantMinor: centimes(Number(mt)), libelle });
      onFini(`Frais bancaires enregistrés — ${montant(centimes(Number(mt)))}.`);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Enregistrement impossible'); }
  }

  return (
    <Modale titre="Frais bancaires" onFermer={onFermer}>
      <p className="mini sourdine" style={{ marginTop: -4, marginBottom: 12 }}>
        Ils sortent du compte, pas de la caisse : la caisse n'est pas touchée.
      </p>
      <Champ label={`Montant (${devise})`}>
        <input type="number" inputMode="decimal" value={mt} onChange={(e) => setMt(e.target.value)} autoFocus />
      </Champ>
      <div style={{ marginTop: 12 }}>
        <Champ label="Libellé"><input value={libelle} onChange={(e) => setLibelle(e.target.value)} /></Champ>
      </div>
      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" disabled={!(Number(mt) > 0)} onClick={() => void envoyer()}>Enregistrer</button>
      </div>
    </Modale>
  );
}
